/**
 * TWO READINGS OF ONE SEA. Never an enum anybody sets.
 *
 * The sea is ONE CONTINUOUS MAP. Neither a "water" nor an "area" is a place the boat enters;
 * both are readings taken of the sea at the boat's coordinates. Two continuous scalar fields
 * are defined over the whole plane and everything below is a reading of THOSE TWO:
 *
 *   openness  0 at the home coast, 1 out at the world radius, wandered by value noise
 *             so the isolines are not circles.
 *   chill     0 in the far south, 1 in the far north, wandered the same way.
 *
 * WHAT IS UNDER THE KEEL is the WATER: shallows, reef, deep, cold. It is a classification of
 * FISHING GROUND and the chart says it out loud ("Over the reef").
 *
 * WHAT IS ABOVE THE WATERLINE is the AREA: the Wrack, the Shell Banks, the Rime, the Glass, the
 * Sunder. It is a classification of CLIMATE AND COUNTRY, and it is what CONTRACT 1.5m asks for.
 *
 * They are not two geographies. They are two questions asked of the same `fieldsAt`, the way
 * temperature and rainfall are two readings of one climate, and they share `nearestIndex` and
 * `blendWeights` below rather than each carrying a copy. In both, the reading at a point is the
 * nearest archetype; the blend weights are a softmax of the same distances, so `regionAt` is
 * exactly `argmax(regionWeightsAt)` and `areaAt` is exactly `argmax(areaWeightsAt)`, by
 * construction, and neither pair can disagree. The weights are what the render lane crossfades,
 * which is why a boundary is a gradient about a frame wide instead of a seam.
 *
 * There is no hysteresis and no stored previous reading, so the classification of a point does
 * not depend on how the boat got there.
 *
 * -- WHY THE WATER TABLE WAS NOT REBALANCED, WHICH IS A MEASUREMENT --------------
 *
 * This file used to say that the four waters were "not yet a division of the sea into areas of
 * comparable weight" and that "adding or moving an archetype is a change to the table below and
 * nothing else". The first half is true. THE SECOND HALF IS FALSE, and it was measured:
 * `src/render/chart-copy.ts` holds `REGION_PHRASE`, a record with exactly the four water ids in
 * it and a `?? 'At sea'` fallback behind it, so a fifth WATER id would reach the player as "At
 * sea" and nothing would go red. A water id is a word on the chart, not only a row in a table.
 *
 * And the four-way split cannot be balanced anyway, because of where the places are. MEASURED
 * over the twenty charted places (`WATER_REGIONS` order):
 *
 *   as shipped, archetypes unmoved      shallows 5   reef 4   deep 10   cold 1
 *
 * MEASURED chill over the same twenty: 1 place above 0.60, 3 above 0.55, 4 above 0.50, median
 * 0.4435. Only three of the twenty lie north of the equator line at all. So an archetype that
 * MEANS cold can never hold more than about three of them. Three rebalances were tried and
 * measured: two land on cold 1 / deep 9-10 again, and the only one that gives cold five members
 * does it by moving the cold archetype to chill 0.55 - a hair above the sea's own median - after
 * which it calls chill-0.446 water cold while chill-0.139 water is deep. Its worst decision
 * margin is 0.0021 field units against the area table's 0.0498, so it is also the most fragile
 * of the four. A table that has to lie to balance is not balanced.
 *
 * THE CONCLUSION, and it is the answer to the question 1.5m actually asks: the water table is
 * RIGHT AS IT IS, because it classifies fishing ground and ten of the twenty places really are
 * over deep water. What 1.5m needs is not a rebalanced water table but a SECOND READING, and
 * that is `SEA_AREAS`. It is measured at 5 / 5 / 1 / 4 / 5 with a worst decision margin of
 * 0.0498 field units - 33.5 leagues at the openness scale - which is twice the shipped water
 * table's 0.0234 and twenty-four times the balanced-by-lying candidate's.
 *
 * THE RIME HAS ONE ISLAND AND THAT IS NOT A DEFECT OF THIS TABLE. Three of twenty places lie
 * north of the equator line and exactly one is properly cold. The places are not moved to fix
 * it - the voyage layer derives discovery, progression and sail time from position and the
 * cadence is load-bearing - so the far north is a singular place rather than a populous region,
 * and `gauntlet/island/REGIONS.md` writes it that way on purpose.
 *
 * WHAT IS NOT ASSUMED ANYWHERE: that every island is the same size. `PlaceSeed.extent` is
 * per-place and optional, so an area whose islands are larger says so per island.
 */

import {
  CHILL_SCALE_LEAGUES,
  FIELD_NOISE_AMPLITUDE_LEAGUES,
  FIELD_NOISE_PERIOD_LEAGUES,
  FIELD_NOISE_SEED,
  OPENNESS_SCALE_LEAGUES,
  REGION_BLEND_TEMPERATURE,
} from './constants';
import { fieldNoise } from './noise';
import type { Vec2 } from './units';
import { clamp01, magnitude } from './units';

export const WATER_REGIONS = ['shallows', 'reef', 'deep', 'cold'] as const;
export type WaterRegionId = (typeof WATER_REGIONS)[number];

/**
 * THE AREAS OF THIS SEA, ordered from home outward. CONTRACT 1.5m(c) and 1.5m(d).
 *
 * The fiction each one carries - what grows there, what the harbour is for, what the weather
 * demands of a building, and which islands belong to it - is written in
 * `gauntlet/island/REGIONS.md`. This file only says where they are.
 */
export const SEA_AREAS = ['wrack', 'shellbanks', 'rime', 'glass', 'sunder'] as const;
export type SeaAreaId = (typeof SEA_AREAS)[number];

export interface WaterFields {
  readonly openness: number;
  readonly chill: number;
}

interface Archetype<Id extends string> {
  readonly id: Id;
  readonly openness: number;
  readonly chill: number;
}

/** MODELLED positions in field space. Order fixes the tie-break: lowest index wins. */
const ARCHETYPES: readonly Archetype<WaterRegionId>[] = [
  { id: 'shallows', openness: 0.1, chill: 0.5 },
  { id: 'reef', openness: 0.42, chill: 0.2 },
  { id: 'deep', openness: 0.88, chill: 0.52 },
  { id: 'cold', openness: 0.75, chill: 0.92 },
];

/**
 * MODELLED positions in field space, chosen against the MEASURED fields of the twenty charted
 * places so that the sea divides into areas of comparable weight WITHOUT MOVING A PLACE.
 *
 * Measured membership at these coordinates: wrack 5, shellbanks 5, rime 1, glass 4, sunder 5.
 * Worst decision margin 0.0498 field units, at Kell Skerry, which is 33.5 leagues of positional
 * slack at the openness scale. `areas.test.ts` re-measures both figures.
 *
 * The chill coordinates are NOT evenly spread, on purpose: the sea's own chill runs 0.013 to
 * 0.841 with a median of 0.4435 and only one place above 0.60, so an evenly spread table would
 * put three archetypes where no island is.
 */
const AREA_ARCHETYPES: readonly Archetype<SeaAreaId>[] = [
  { id: 'wrack', openness: 0.16, chill: 0.52 },
  { id: 'shellbanks', openness: 0.52, chill: 0.4 },
  { id: 'rime', openness: 0.68, chill: 0.86 },
  { id: 'glass', openness: 0.92, chill: 0.15 },
  { id: 'sunder', openness: 0.87, chill: 0.45 },
];

export function fieldsAt(p: Vec2): WaterFields {
  const n1 = fieldNoise(p.x, p.y, FIELD_NOISE_PERIOD_LEAGUES, FIELD_NOISE_SEED);
  const n2 = fieldNoise(p.x + 911.5, p.y - 517.25, FIELD_NOISE_PERIOD_LEAGUES, FIELD_NOISE_SEED ^ 0x27d4eb2f);
  const wobble1 = (n1 - 0.5) * 2 * FIELD_NOISE_AMPLITUDE_LEAGUES;
  const wobble2 = (n2 - 0.5) * 2 * FIELD_NOISE_AMPLITUDE_LEAGUES;
  return {
    openness: clamp01((magnitude(p) + wobble1) / OPENNESS_SCALE_LEAGUES),
    chill: clamp01(0.5 + (p.y + wobble2) / CHILL_SCALE_LEAGUES),
  };
}

function squaredFieldDistances<Id extends string>(
  f: WaterFields,
  table: readonly Archetype<Id>[],
): number[] {
  const out: number[] = [];
  for (const a of table) {
    const do_ = f.openness - a.openness;
    const dc = f.chill - a.chill;
    out.push(do_ * do_ + dc * dc);
  }
  return out;
}

/**
 * Blend weights over a table, summing to 1. Softmax of negative squared field distance,
 * shifted by the minimum before exponentiating so nothing underflows and the result is
 * identical however far the point is from every archetype.
 */
function blendWeights(d2: readonly number[]): readonly number[] {
  let min = Infinity;
  for (const v of d2) if (v < min) min = v;

  const raw: number[] = [];
  let total = 0;
  for (const v of d2) {
    const w = Math.exp(-(v - min) / REGION_BLEND_TEMPERATURE);
    raw.push(w);
    total += w;
  }
  const out: number[] = [];
  for (const w of raw) out.push(w / total);
  return out;
}

/** Index of the nearest archetype. Ties resolve to the lower index, deterministically. */
function nearestIndex(d2: readonly number[]): number {
  let best = 0;
  let bestVal = Infinity;
  for (let i = 0; i < d2.length; i += 1) {
    const v = d2[i] ?? Infinity;
    if (v < bestVal) {
      bestVal = v;
      best = i;
    }
  }
  return best;
}

/** Blend weights over the four waters, summing to 1. */
export function regionWeightsAt(p: Vec2): readonly number[] {
  return blendWeights(squaredFieldDistances(fieldsAt(p), ARCHETYPES));
}

/** Index of the water at a point. Ties resolve to the lower index, deterministically. */
export function regionIndexAt(p: Vec2): number {
  return nearestIndex(squaredFieldDistances(fieldsAt(p), ARCHETYPES));
}

export function regionAt(p: Vec2): WaterRegionId {
  return WATER_REGIONS[regionIndexAt(p)] ?? 'shallows';
}

/** Blend weights over the five areas, summing to 1. Same softmax, same temperature. */
export function areaWeightsAt(p: Vec2): readonly number[] {
  return blendWeights(squaredFieldDistances(fieldsAt(p), AREA_ARCHETYPES));
}

/** Index of the area at a point. Ties resolve to the lower index, deterministically. */
export function areaIndexAt(p: Vec2): number {
  return nearestIndex(squaredFieldDistances(fieldsAt(p), AREA_ARCHETYPES));
}

/** Which part of the world this is: climate, country, and what its islands are made of. */
export function areaAt(p: Vec2): SeaAreaId {
  return SEA_AREAS[areaIndexAt(p)] ?? 'wrack';
}

/**
 * The area archetype coordinates, published so a test can re-measure the decision margin rather
 * than trust the figure in the docstring above. Not read by the game.
 */
export const AREA_ARCHETYPE_FIELDS: ReadonlyMap<SeaAreaId, WaterFields> = new Map(
  AREA_ARCHETYPES.map((a) => [a.id, { openness: a.openness, chill: a.chill }]),
);
